<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, watch } from 'vue';
import { z } from 'zod';

import ModalForm from '~/components/shared/ModalForm.vue';

interface Props {
  fieldLabel?: string;
  isLoading?: boolean;
  maxLength?: number;
  minLength?: number;
  modelValue: boolean;
  placeholder: string;
  primaryButtonText: string;
  title: string;
}

const props = withDefaults(defineProps<Props>(), {
  fieldLabel: 'Motivo',
  isLoading: false,
  maxLength: 500,
  minLength: 10,
});

const emit = defineEmits<{
  'confirm': [data: { reason: string }];
  'update:modelValue': [value: boolean];
}>();

const reasonSchema = z.object({
  reason: z
    .string()
    .min(1, 'Motivo é obrigatório')
    .min(props.minLength, `Motivo deve ter pelo menos ${props.minLength} caracteres`)
    .max(props.maxLength, `Motivo deve ter no máximo ${props.maxLength} caracteres`)
    .refine(
      (val) => val.trim().length >= props.minLength,
      `Motivo deve ter pelo menos ${props.minLength} caracteres válidos`,
    ),
});

type ReasonFormData = z.infer<typeof reasonSchema>;

const {
  defineField,
  errors,
  handleSubmit,
  resetForm,
  setFieldError,
} = useForm<ReasonFormData>({
  initialValues: {
    reason: '',
  },
  validateOnMount: false,
  validationSchema: toTypedSchema(reasonSchema),
});

const [reason, reasonProps] = defineField('reason');

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const characterCount = computed(() => reason.value?.length || 0);

const clearReasonError = () => setFieldError('reason', undefined);

const validateReason = async () => {
  try {
    setFieldError('reason', undefined);
    await reasonSchema.parseAsync({ reason: reason.value });
  } catch (error: any) {
    const fieldError = error.errors?.find((err: any) => err.path[0] === 'reason');
    if (fieldError) setFieldError('reason', fieldError.message);
  }
};

const handleSave = handleSubmit(async (formValues) => {
  emit('confirm', { reason: formValues.reason.trim() });
});

const handleCancel = () => {
  isVisible.value = false;
};

// Reset form when modal closes
watch(() => props.modelValue, (isOpen) => {
  if (!isOpen) {
    resetForm();
  }
});
</script>

<template>
  <ModalForm
    v-model="isVisible"
    :title="title"
    mode="create"
    :loading="isLoading"
    :primary-button-text="primaryButtonText"
    size="md"
    @save="handleSave"
    @cancel="handleCancel"
  >
    <template #default="{ isViewMode, isLoading: modalIsLoading }">
      <div class="mb-3">
        <label class="form-label required" for="reason">
          {{ fieldLabel }}
        </label>
        <textarea
          id="reason"
          v-model="reason"
          v-bind="reasonProps"
          class="form-control"
          :class="{ 'is-invalid': errors.reason }"
          :disabled="isViewMode || modalIsLoading"
          :placeholder="placeholder"
          rows="4"
          :maxlength="maxLength"
          @input="clearReasonError"
          @blur="validateReason"
        />
        <div class="d-flex justify-content-between align-items-start gap-2 mt-1">
          <div v-if="errors.reason" class="invalid-feedback d-block mb-0">
            {{ errors.reason }}
          </div>
          <small class="text-muted ms-auto character-count">
            {{ characterCount }}/{{ maxLength }} caracteres
          </small>
        </div>
      </div>
    </template>
  </ModalForm>
</template>

<style scoped lang="scss">
.form-control {
  resize: vertical;
  min-height: 100px;
}

.invalid-feedback {
  font-size: 0.875rem;
}

.character-count {
  flex: 0 0 auto;
  white-space: nowrap;
}
</style>
